import { useMemo } from 'react';
import { groupByLogicalKey } from '../components/ProposalDashboard/v2/amendment/groupByLogicalKey';
import { useCLINs } from './useCLINs';
/**
 * useCLINHistory returns the amendment-version chain for a single CLIN,
 * derived from the project's full CLIN list.
 *
 * Mirrors useLcatHistory exactly — same shape, same fallback behavior.
 * The hook reuses the existing useCLINs cache (no new RPC). Given a
 * representative CLIN row it returns:
 *   - `versions`: every row sharing that CLIN's logical key, latest first.
 *   - `current`: the canonical / current version.
 *   - `hasHistory`: true when there's more than one version. The drawer
 *     uses this to decide whether to render the timeline at all.
 *
 * Falls back to matching by `clinNumber` when `logicalKey` isn't set
 * (legacy / non-amendment-aware projects).
 */ export function useCLINHistory(projectId, selected) {
    const { data } = useCLINs(projectId);
    return useMemo(()=>{
        if (!selected || !data?.clins) {
            return {
                versions: [],
                current: null,
                hasHistory: false
            };
        }
        // Prefer logicalKey for grouping (set by the merge step's
        // collapseCLINVersions). Fall back to clinNumber match when the
        // field isn't populated.
        const grouped = groupByLogicalKey(data.clins);
        let key = selected.logicalKey?.trim();
        if (!key) {
            const numNorm = (selected.clinNumber ?? '').trim();
            for (const k of grouped.allKeys){
                const head = grouped.currentByKey.get(k);
                if (head && (head.clinNumber ?? '').trim() === numNorm) {
                    key = k;
                    break;
                }
            }
        }
        if (!key) {
            return {
                versions: [
                    selected
                ],
                current: selected,
                hasHistory: false
            };
        }
        const versions = grouped.historyByKey.get(key) ?? [
            selected
        ];
        const current = grouped.currentByKey.get(key) ?? selected;
        return {
            versions,
            current,
            hasHistory: versions.length > 1
        };
    }, [
        data?.clins,
        selected
    ]);
}
